import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  StreamVideo, StreamCall,
  ParticipantView, useCallStateHooks,
} from "@stream-io/video-react-sdk";
import "@stream-io/video-react-sdk/dist/css/styles.css";
import {
  ArrowLeft, Microphone, MicrophoneSlash, VideoCamera, VideoCameraSlash,
  X, UsersThree, Waveform, PaperPlaneTilt, ClosedCaptioning, Download, Sparkle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { getStreamClients } from "../lib/streamClient";
import {
  installVoiceEnhancer, setVoiceEnhancement, isVoiceEnhancementEnabled,
} from "../lib/voiceEnhancer";
import useRoomCaptions from "../lib/useRoomCaptions";

/**
 * RoomView — join / host a multi-participant Stream Video meeting room.
 *
 * Flow:
 *   1. `/api/rooms/:id` (GET) → fetch metadata + `can_join` verdict.
 *   2. `/api/rooms/:id/join` (POST) → verify access, charge waves for paid rooms.
 *   3. Establish Stream call via cached client, join with camera + mic.
 *   4. Show a responsive participant grid with mute / camera / leave controls
 *      + a voice-enhancement toggle (WebAudio-based).
 *   5. Host also sees an "End Room" button that ends for everyone.
 */

function ParticipantGrid() {
  const { useParticipants } = useCallStateHooks();
  const participants = useParticipants();
  const count = participants.length;
  const cols = count <= 1 ? 1 : count <= 4 ? 2 : count <= 9 ? 3 : 4;
  return (
    <div className={`grid gap-2 flex-1 min-h-0`} style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }} data-testid="room-participants">
      {participants.map((p) => (
        <div key={p.sessionId || p.userId} className="rounded-2xl overflow-hidden bg-black/60 border border-white/10 min-h-[8rem]">
          <ParticipantView participant={p} ParticipantViewUI={null} />
        </div>
      ))}
      {participants.length === 0 && (
        <div className="col-span-full flex items-center justify-center text-cream/40 text-xs tracking-[0.25em] uppercase animate-pulse">
          Waiting for participants…
        </div>
      )}
    </div>
  );
}

function ControlBar({ call, isHost, onEnd, onLeave, voiceOn, toggleVoice, captionsOn, toggleCaptions, supportsCaptions }) {
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);

  const toggleMic = async () => {
    try {
      if (micOn) await call.microphone.disable();
      else await call.microphone.enable();
      setMicOn(!micOn);
    } catch (e) { toast.error("Mic toggle failed"); }
  };
  const toggleCam = async () => {
    try {
      if (camOn) await call.camera.disable();
      else await call.camera.enable();
      setCamOn(!camOn);
    } catch (e) { toast.error("Camera toggle failed"); }
  };

  return (
    <div className="p-3 flex items-center justify-center gap-2 bg-ink border-t border-white/10" data-testid="room-controls">
      <button onClick={toggleMic} className={`w-12 h-12 rounded-full ${micOn ? "crystal-glass text-cream" : "bg-red-500/20 text-red-300"} flex items-center justify-center`} data-testid="room-mic">
        {micOn ? <Microphone size={20} weight="fill" /> : <MicrophoneSlash size={20} weight="fill" />}
      </button>
      <button onClick={toggleCam} className={`w-12 h-12 rounded-full ${camOn ? "crystal-glass text-cream" : "bg-red-500/20 text-red-300"} flex items-center justify-center`} data-testid="room-cam">
        {camOn ? <VideoCamera size={20} weight="fill" /> : <VideoCameraSlash size={20} weight="fill" />}
      </button>
      <button onClick={toggleVoice} className={`w-12 h-12 rounded-full ${voiceOn ? "bg-papaya/25 text-papaya papaya-glow" : "crystal-glass text-cream/60"} flex items-center justify-center`} data-testid="room-voice">
        <Waveform size={20} weight={voiceOn ? "fill" : "regular"} />
      </button>
      {supportsCaptions && isHost && (
        <button
          onClick={toggleCaptions}
          className={`w-12 h-12 rounded-full ${
            captionsOn ? "bg-teal-500/25 text-teal-300" : "crystal-glass text-cream/60"
          } flex items-center justify-center`}
          title={captionsOn ? "Captions ON — press to stop" : "Turn on live captions"}
          data-testid="room-captions-toggle"
        >
          <ClosedCaptioning size={20} weight={captionsOn ? "fill" : "regular"} />
        </button>
      )}
      {isHost ? (
        <button onClick={onEnd} className="ml-2 px-4 h-12 rounded-full bg-red-500 text-cream text-[11px] font-black tracking-[0.2em] uppercase flex items-center gap-2" data-testid="room-end">
          <X size={16} weight="bold" /> End Room
        </button>
      ) : (
        <button onClick={onLeave} className="ml-2 px-4 h-12 rounded-full bg-white/10 text-cream text-[11px] font-black tracking-[0.2em] uppercase flex items-center gap-2" data-testid="room-leave">
          Leave
        </button>
      )}
    </div>
  );
}

export default function RoomView() {
  const { id } = useParams();
  const nav = useNavigate();
  const [phase, setPhase] = useState("loading"); // loading | preview | live | error
  const [room, setRoom] = useState(null);
  const [videoClient, setVideoClient] = useState(null);
  const [call, setCall] = useState(null);
  const [isHost, setIsHost] = useState(false);
  const [voiceOn, setVoiceOn] = useState(true);
  const [captionsOn, setCaptionsOn] = useState(false);
  const [summary, setSummary] = useState(null);
  const [summarizing, setSummarizing] = useState(false);
  const callRef = useRef(null);

  const supportsCaptions = !!room?.supports_captions;
  const {
    captions, active: captionsActive,
    start: startCaptionsRec, stop: stopCaptionsRec,
  } = useRoomCaptions({
    roomId: id, isHost, enabled: captionsOn && supportsCaptions,
    canCaption: supportsCaptions,
  });

  // Install enhancer once
  useEffect(() => {
    installVoiceEnhancer(); setVoiceEnhancement(true);
    return () => {
      if (callRef.current) callRef.current.leave().catch(() => {});
    };
  }, []);

  // Fetch room metadata
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/rooms/${id}`);
        setRoom(data);
        if (!data.is_active) { toast.error("This room has ended."); nav("/rooms"); return; }
        setPhase("preview");
      } catch (e) { toast.error("Room not found"); nav("/rooms"); }
    })();
  }, [id, nav]);

  const joinRoom = async () => {
    try {
      const [{ videoClient: v }, { data: joinData }] = await Promise.all([
        getStreamClients(),
        api.post(`/rooms/${id}/join`, {}),
      ]);
      setVideoClient(v);
      setIsHost(!!joinData.is_host);
      const c = v.call(joinData.call_type, joinData.call_id);
      callRef.current = c;
      await c.join({ create: true });
      await c.camera.enable();
      await c.microphone.enable();
      setCall(c);
      setPhase("live");
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || "Could not join room";
      toast.error(detail);
      if (e?.response?.status === 402 || e?.response?.status === 403) {
        // Cannot join — bounce back to list.
        setTimeout(() => nav("/rooms"), 1500);
      }
    }
  };

  const leaveRoom = async () => {
    try {
      if (call) await call.leave();
      callRef.current = null;
      await api.post(`/rooms/${id}/leave`);
    } catch (e) { /* ignore */ }
    nav("/rooms");
  };

  const endRoom = async () => {
    try {
      stopCaptionsRec();
      if (call) await call.leave();
      callRef.current = null;
      await api.post(`/rooms/${id}/end`);
    } catch (e) { /* ignore */ }

    // Only try to summarize if this is a premium room + we actually captured any captions.
    if (supportsCaptions) {
      setSummarizing(true);
      try {
        const { data } = await api.post(`/rooms/${id}/summary`);
        setSummary(data);
        toast.success("Meeting summary ready.");
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Summary generation failed");
      } finally { setSummarizing(false); }
    } else {
      toast.success("Room ended.");
      nav("/rooms");
    }
  };

  const toggleCaptions = async () => {
    if (!supportsCaptions) return;
    const next = !captionsOn;
    setCaptionsOn(next);
    if (next) {
      await startCaptionsRec();
      toast.success("Live captions ON — everyone in the room will see them.");
    } else {
      stopCaptionsRec();
      toast.info("Captions paused.");
    }
  };

  const downloadTranscript = async () => {
    try {
      const resp = await api.get(`/rooms/${id}/transcript.txt`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = `giffwurldaunce-transcript-${id.slice(0, 8)}.txt`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Transcript download failed"); }
  };

  const toggleVoice = async () => {
    const next = !voiceOn;
    setVoiceOn(next);
    setVoiceEnhancement(next);
    try {
      if (call?.microphone) {
        await call.microphone.disable();
        await call.microphone.enable();
      }
      toast.success(next ? "Voice enhancement ON" : "Voice enhancement OFF");
    } catch (e) { /* ignore */ }
  };

  if (phase === "loading" || !room) {
    return (
      <div className="min-h-[100dvh] bg-ink flex items-center justify-center">
        <p className="text-cream/40 text-xs tracking-[0.3em] uppercase animate-pulse">Loading room…</p>
      </div>
    );
  }

  // Preview / entry screen — shown before we call /join (allows previewing paid rooms)
  if (phase === "preview") {
    const verdict = room.can_join || {};
    return (
      <div className="min-h-[100dvh] bg-ink pb-32 px-5 pt-6" data-testid="room-preview">
        <button onClick={() => nav("/rooms")} className="w-10 h-10 rounded-full crystal-glass flex items-center justify-center text-cream mb-4">
          <ArrowLeft size={18} />
        </button>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-2xl bg-papaya/15 text-papaya flex items-center justify-center">
            <UsersThree size={26} weight="fill" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-papaya text-[10px] tracking-[0.3em] uppercase font-bold">{room.room_type} · {room.category}</p>
            <h1 className="text-cream text-2xl font-black tracking-tighter truncate">{room.title}</h1>
          </div>
        </div>
        <p className="text-cream/70 text-sm">{room.description || "—"}</p>
        <div className="mt-4 rounded-2xl bg-white/5 border border-white/10 p-3 flex items-center gap-3">
          <span className="text-[10px] tracking-[0.25em] uppercase text-cream/60">host</span>
          <span className="text-cream font-bold">@{room.host_username || room.host_display_name}</span>
          <span className="ml-auto text-papaya text-[11px] tracking-[0.2em] uppercase font-black">
            {room.participants_count || 0}/{room.max_participants || 12} in
          </span>
        </div>
        {room.room_type === "paid" && (
          <p className="mt-3 rounded-2xl bg-jam-gold/10 border border-jam-gold/30 text-jam-gold p-3 text-[11px] tracking-[0.2em] uppercase font-black" data-testid="room-price-badge">
            Entry · {room.price_waves} waves
          </p>
        )}
        {room.supports_captions && (
          <p className="mt-2 rounded-2xl bg-teal-500/10 border border-teal-500/30 text-teal-300 p-3 text-[11px] tracking-[0.25em] uppercase font-black flex items-center gap-2" data-testid="room-captions-badge">
            <span>CC</span>
            Live AI captions + meeting summary included
          </p>
        )}
        <button
          onClick={joinRoom}
          disabled={verdict.ok === false && verdict.reason !== "host"}
          className="w-full mt-6 py-4 rounded-full bg-papaya text-ink font-black tracking-[0.2em] uppercase text-sm hover:brightness-110 transition disabled:opacity-40 flex items-center justify-center gap-2"
          data-testid="room-join-btn"
        >
          <PaperPlaneTilt size={18} weight="fill" /> {verdict.reason === "host" ? "Enter as Host" : "Join Room"}
        </button>
        {verdict.ok === false && (
          <p className="mt-3 text-cream/50 text-xs text-center" data-testid="room-cannot-join">
            {verdict.reason === "auth_required" && "Log in first."}
            {verdict.reason === "private_no_invite" && "This is a private room — ask the host for an invite."}
            {verdict.reason === "invite_required" && "Ask the host for an invite."}
            {verdict.reason === "insufficient_waves" && `Need ${verdict.price_waves} waves to enter.`}
            {verdict.reason === "subscription_required" && "Active VIP or Voice Pro subscription required."}
          </p>
        )}
      </div>
    );
  }

  // Live room
  return (
    <StreamVideo client={videoClient}>
      {call && (
        <StreamCall call={call}>
          <div className="h-[100dvh] bg-black flex flex-col" data-testid="room-live">
            <div className="p-3 flex items-center justify-between bg-ink border-b border-white/10">
              <div className="min-w-0">
                <p className="text-papaya text-[9px] tracking-[0.3em] uppercase font-bold">{room.room_type} · {room.category}</p>
                <p className="text-cream font-black truncate">{room.title}</p>
              </div>
              <span className="text-cream/60 text-[10px] tracking-[0.3em] uppercase">
                {isHost ? "Host" : "Participant"}
              </span>
            </div>
            <div className="flex-1 p-2 min-h-0 relative">
              <ParticipantGrid />

              {captionsOn && captions.length > 0 && (
                <div
                  className="absolute left-1/2 -translate-x-1/2 bottom-2 max-w-[92%] w-full"
                  data-testid="room-captions-overlay"
                >
                  <div className="rounded-2xl bg-black/70 backdrop-blur px-4 py-2 text-center">
                    {captions.slice(-2).map((c) => (
                      <p key={c.id} className="text-cream text-sm leading-snug">
                        <span className="text-papaya text-[10px] tracking-[0.2em] uppercase font-black mr-2">
                          {(c.speaker_display_name || c.speaker_username || "").slice(0, 18)}
                        </span>
                        {c.text}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <ControlBar
              call={call}
              isHost={isHost}
              onEnd={endRoom}
              onLeave={leaveRoom}
              voiceOn={voiceOn}
              toggleVoice={toggleVoice}
              captionsOn={captionsOn}
              toggleCaptions={toggleCaptions}
              supportsCaptions={supportsCaptions}
            />
          </div>
        </StreamCall>
      )}

      {(summary || summarizing) && (
        <SummaryModal
          summary={summary}
          summarizing={summarizing}
          canDownload={isHost}
          onDownload={downloadTranscript}
          onClose={() => nav("/rooms")}
        />
      )}
    </StreamVideo>
  );
}

function SummaryModal({ summary, summarizing, canDownload, onDownload, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50" data-testid="room-summary-modal">
      <div className="w-full max-w-lg rounded-3xl crystal-glass p-6 max-h-[90dvh] overflow-y-auto">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-2xl bg-papaya/15 text-papaya flex items-center justify-center">
            <Sparkle size={22} weight="fill" />
          </div>
          <div>
            <p className="text-papaya text-[10px] tracking-[0.3em] uppercase font-bold">Room ended</p>
            <h2 className="text-cream text-xl font-black tracking-tighter">
              {summarizing ? "Generating meeting summary…" : "Meeting Summary"}
            </h2>
          </div>
        </div>
        {summarizing ? (
          <div className="rounded-2xl bg-white/5 border border-white/10 p-6 text-center">
            <div className="animate-pulse text-cream/60 text-sm">
              Claude is analyzing the transcript…
            </div>
          </div>
        ) : summary ? (
          <>
            <p className="text-cream/50 text-[11px] tracking-[0.25em] uppercase font-bold mb-1">
              {summary.title} · {summary.transcript_chars} chars transcribed
            </p>
            <div
              className="prose prose-invert prose-sm max-w-none text-cream/90 whitespace-pre-wrap"
              data-testid="room-summary-body"
            >
              {summary.summary_markdown}
            </div>
          </>
        ) : null}
        <div className="mt-5 flex flex-wrap gap-2">
          {canDownload && !summarizing && (
            <button
              onClick={onDownload}
              className="rounded-full bg-white/10 text-cream text-[11px] px-4 py-2 font-black tracking-[0.2em] uppercase hover:bg-white/20 flex items-center gap-2"
              data-testid="room-summary-download"
            >
              <Download size={14} weight="bold" /> Transcript
            </button>
          )}
          <button
            onClick={onClose}
            className="ml-auto rounded-full bg-papaya text-ink text-[11px] px-4 py-2 font-black tracking-[0.2em] uppercase hover:brightness-110"
            data-testid="room-summary-close"
          >Done</button>
        </div>
      </div>
    </div>
  );
}
